type Control = HTMLInputElement | HTMLSelectElement | HTMLTextAreaElement;
type Translate = (ar: string, en: string) => string;
type Options = { valid: boolean; validationMessage?: string; pending?: boolean; w: Translate };
type Issue = { control: HTMLElement; visual: HTMLElement; anchor: HTMLElement; message: string; name: string };
type Feedback = { issue: Issue; node: HTMLElement; previousInvalid: string | null };
let sequence = 0;

function isControl(node: Element): node is Control {
  return node instanceof HTMLInputElement || node instanceof HTMLSelectElement || node instanceof HTMLTextAreaElement;
}
function visualFor(control: HTMLElement) {
  if (control instanceof HTMLInputElement && control.type === "radio" && control.name) {
    const fieldset = control.closest("fieldset");
    const legend = fieldset?.querySelector(":scope > legend");
    if (fieldset && legend) return { visual: fieldset, anchor: fieldset, control };
  }
  const group = control.closest<HTMLElement>(".app-phone-input, .otp-fields, .mzn-date-time, .mzn-select");
  const visual = group ?? control;
  const focus = !control.matches('[aria-hidden="true"], [type="hidden"]') && isControl(control) ? control : group?.querySelector<HTMLElement>('input:not([aria-hidden="true"]):not([type="hidden"]), button[id], [role="combobox"]') ?? control;
  const anchor = visual.closest<HTMLElement>(".app-phone-field") ?? visual;
  return { visual, control: focus, anchor };
}
function fieldLabel(control: HTMLElement): HTMLElement | null {
  const labelledBy = control.getAttribute("aria-labelledby") || control.closest("[role=group]")?.getAttribute("aria-labelledby");
  if (labelledBy) return document.getElementById(labelledBy.split(/\s+/)[0]);
  return (isControl(control) ? control.labels?.[0] : control.id ? document.querySelector<HTMLLabelElement>(`label[for="${CSS.escape(control.id)}"]`) : null) ?? control.closest("label");
}
function fieldName(control: HTMLElement, w: Translate) {
  if (control.getAttribute("aria-label")) return control.getAttribute("aria-label")!;
  const label = fieldLabel(control)?.cloneNode(true) as HTMLElement | undefined;
  label?.querySelectorAll("input, select, textarea, button, .mzn-select, .mzn-date-time, .app-phone-field, .field-requirement, [data-form-feedback]").forEach(node => node.remove());
  return label?.textContent?.trim() || w("الحقل", "Field");
}
function messageFor(input: Control, w: Translate): string | undefined {
  if (!input.willValidate) return;
  const v = input.validity;
  if (v.valueMissing || input.required && !["checkbox", "radio", "file"].includes(input.type) && !input.value.trim())
    return w("هذا الحقل إلزامي.", "This field is required.");
  if (input.dataset.validationError) return input.dataset.validationError;
  if (v.typeMismatch) return input.type === "email" ? w("أدخل بريدًا إلكترونيًا صحيحًا.", "Enter a valid email address.") : w("أدخل رابطًا صحيحًا.", "Enter a valid URL.");
  if (v.badInput) return w("أدخل رقمًا صحيحًا.", "Enter a valid number.");
  if (input instanceof HTMLInputElement && v.rangeUnderflow) return w(`أدخل قيمة لا تقل عن ${input.min}.`, `Enter a value of at least ${input.min}.`);
  if (input instanceof HTMLInputElement && v.rangeOverflow) return w(`أدخل قيمة لا تزيد عن ${input.max}.`, `Enter a value no greater than ${input.max}.`);
  if (v.stepMismatch) return w("أدخل قيمة وفق الزيادة المسموح بها.", "Enter a value using the allowed increment.");
  if (!(input instanceof HTMLSelectElement) && input.value) {
    if (input.minLength > 0 && input.value.length < input.minLength) return w(`أدخل ${input.minLength} أحرف على الأقل.`, `Enter at least ${input.minLength} characters.`);
    if (input.maxLength >= 0 && input.value.length > input.maxLength) return w(`الحد الأقصى ${input.maxLength} حرفًا.`, `Use no more than ${input.maxLength} characters.`);
  }
  if (input instanceof HTMLInputElement && input.inputMode === "decimal" && input.value && !/^[+-]?[0-9]+(?:\.[0-9]+)?$/.test(input.value))
    return w("أدخل رقمًا باستخدام النقطة للفاصلة العشرية.", "Enter a number using a dot for the decimal separator.");
  if (v.patternMismatch) return input.title || w("أدخل قيمة بالتنسيق المطلوب.", "Enter a value in the required format.");
  if (!v.valid) return v.customError ? input.validationMessage : w("راجع قيمة هذا الحقل.", "Check this field's value.");
}
function focusIssue(issue: Issue) {
  for (let node: HTMLElement | null = issue.control; node; node = node.parentElement) if (node instanceof HTMLDetailsElement) node.open = true;
  issue.control.focus({ preventScroll: true });
  issue.visual.scrollIntoView({ block: "nearest", behavior: "instant" });
}

/** Scoped DOM feedback supports native and library controls without replacing their values or handlers. */
export function createFormValidation(form: HTMLFormElement, options: () => Options) {
  let attempted = false, frame = 0;
  const feedback = new Map<HTMLElement, Feedback>();
  const markers = new Map<HTMLElement, HTMLElement>();
  const summary = document.createElement("div");
  summary.className = "form-validation-summary";
  summary.dataset.formFeedback = "summary";
  summary.setAttribute("role", "alert");
  summary.tabIndex = -1;
  const observer = new MutationObserver(schedule);
  function observe() {
    observer.observe(form, { subtree: true, childList: true, attributes: true, attributeFilter: ["required", "disabled", "value", "data-field-required", "data-validation-error", "aria-required", "aria-invalid", "min", "max", "pattern"] });
  }
  function schedule() {
    cancelAnimationFrame(frame);
    frame = requestAnimationFrame(() => refresh());
  }
  function remove(item: Feedback) {
    item.node.remove();
    item.issue.visual.removeAttribute("data-form-invalid");
    if (item.issue.control.getAttribute("aria-invalid") === "true") {
      if (item.previousInvalid === null) item.issue.control.removeAttribute("aria-invalid");
      else item.issue.control.setAttribute("aria-invalid", item.previousInvalid);
    }
    const ids = (item.issue.control.getAttribute("aria-describedby") || "").split(/\s+/).filter(id => id && id !== item.node.id);
    if (ids.length) item.issue.control.setAttribute("aria-describedby", ids.join(" ")); else item.issue.control.removeAttribute("aria-describedby");
  }
  function annotate() {
    const requiredLabels = new Set<HTMLElement>();
    for (const node of form.querySelectorAll<HTMLElement>('[required], [aria-required="true"], [data-field-required="true"]')) {
      const target = visualFor(node).control;
      const label = fieldLabel(target);
      if (!label) continue;
      requiredLabels.add(label);
      if (label.querySelector('.field-requirement:not([data-auto-required])')) continue;
      let marker = markers.get(label);
      if (!marker) {
        marker = document.createElement("span"); marker.className = "field-requirement";
        marker.dataset.required = "true"; marker.dataset.autoRequired = "true"; marker.setAttribute("aria-hidden", "true");
        const caption = label.querySelector(".field-caption");
        if (caption) caption.append(marker);
        else {
          let child: HTMLElement | null = target;
          while (child && child.parentElement !== label) child = child.parentElement;
          label.insertBefore(marker, child);
        }
        markers.set(label, marker);
      }
      marker.dataset.label = "*"; marker.title = options().w("إلزامي", "Required");
    }
    for (const [label, marker] of markers) if (!requiredLabels.has(label) || label.querySelector('.field-requirement:not([data-auto-required])')) { marker.remove(); markers.delete(label); }
  }
  function collect(): Issue[] {
    const { w } = options();
    const issues = new Map<HTMLElement, Issue>();
    for (const element of Array.from(form.elements)) {
      if (!isControl(element)) continue;
      const message = messageFor(element, w);
      if (!message) continue;
      const target = visualFor(element);
      if (!issues.has(target.visual)) issues.set(target.visual, { ...target, message, name: fieldName(target.control, w) });
    }
    // Library date inputs are buttons rather than native constraint-validation inputs.
    for (const element of form.querySelectorAll<HTMLElement>('[data-validation-error]')) {
      if (isControl(element) && !element.willValidate) continue;
      if (!element.dataset.validationError || element.closest('fieldset[disabled]') || element.matches(':disabled')) continue;
      const target = visualFor(element);
      if (!issues.has(target.visual)) issues.set(target.visual, { ...target, message: element.dataset.validationError, name: fieldName(target.control, w) });
    }
    return [...issues.values()];
  }
  function refresh() {
    observer.disconnect();
    annotate();
    const { w, valid, validationMessage, pending } = options();
    const issues = attempted && !pending ? collect() : [];
    for (const [visual, item] of feedback) if (!issues.some(issue => issue.visual === visual && issue.control === item.issue.control)) { remove(item); feedback.delete(visual); }
    for (const issue of issues) {
      let item = feedback.get(issue.visual);
      if (!item) {
        const node = document.createElement("span"); node.id = `form-field-error-${++sequence}`; node.className = "form-field-error"; node.dataset.formFeedback = "field"; node.setAttribute("aria-hidden", "true");
        issue.anchor.after(node);
        item = { issue, node, previousInvalid: issue.control.getAttribute("aria-invalid") }; feedback.set(issue.visual, item);
      }
      item.issue = issue;
      if (!item.node.isConnected) issue.anchor.after(item.node);
      if (item.node.textContent !== issue.message) item.node.textContent = issue.message;
      issue.visual.dataset.formInvalid = "true";
      issue.control.setAttribute("aria-invalid", "true");
      const ids = new Set((issue.control.getAttribute("aria-describedby") || "").split(/\s+/).filter(Boolean)); ids.add(item.node.id);
      issue.control.setAttribute("aria-describedby", [...ids].join(" "));
    }
    if (attempted && !pending && (issues.length || !valid)) {
      const heading = issues.length ? w("راجع الحقول المحددة قبل المتابعة.", "Review the highlighted fields before continuing.") : validationMessage || w("راجع بيانات النموذج وشروطه قبل المتابعة.", "Review the form details and requirements before continuing.");
      const signature = JSON.stringify([heading, ...issues.map(issue => [issue.name, issue.message, feedback.get(issue.visual)!.node.id])]);
      if (summary.dataset.signature !== signature) {
        summary.dataset.signature = signature; summary.replaceChildren();
        const title = document.createElement("p"); title.textContent = heading; summary.append(title);
        if (issues.length) {
          const list = document.createElement("ul");
          for (const issue of issues) { const row = document.createElement("li"), button = document.createElement("button"); button.type = "button"; button.textContent = `${issue.name}: ${issue.message}`; button.onclick = () => focusIssue(issue); row.append(button); list.append(row); }
          summary.append(list);
        }
      }
      if (!summary.isConnected) form.prepend(summary);
    } else summary.remove();
    observe();
    return issues;
  }
  function reset() {
    attempted = false; refresh();
  }
  function validate() {
    if (options().pending) return false;
    attempted = true;
    const issues = refresh();
    if (issues.length) focusIssue(issues[0]);
    else if (!options().valid) summary.focus();
    return !issues.length && options().valid;
  }
  form.addEventListener("input", schedule); form.addEventListener("change", schedule);
  refresh();
  return { refresh, validate, reset, destroy() {
    observer.disconnect(); cancelAnimationFrame(frame);
    form.removeEventListener("input", schedule); form.removeEventListener("change", schedule);
    for (const item of feedback.values()) remove(item);
    for (const marker of markers.values()) marker.remove();
    summary.remove();
  } };
}
